import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Wrench, MapPin, Truck, Warning, Timer } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = {
  revision_soon: { Icon: Wrench, cls: "text-amber-600 bg-amber-50" },
  revision_due: { Icon: Warning, cls: "text-red-600 bg-red-50" },
  geofence_enter: { Icon: MapPin, cls: "text-emerald-600 bg-emerald-50" },
  geofence_exit: { Icon: Truck, cls: "text-blue-600 bg-blue-50" },
  detention_request: { Icon: Timer, cls: "text-amber-600 bg-amber-50" },
  detention_hint: { Icon: Timer, cls: "text-amber-600 bg-amber-50" },
};

const timeAgo = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (m < 60) return `${m} min atrás`;
  if (m < 1440) return `${Math.round(m / 60)} h atrás`;
  return new Date(iso).toLocaleDateString("pt-BR");
};

export function AlertsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/alerts").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const h = (e) => {
      if (e.detail?.entity !== "alert") return;
      toast.warning(`${e.detail.data?.title}: ${e.detail.data?.message}`, { duration: 10000 });
      load();
    };
    window.addEventListener("live-event", h);
    return () => { clearInterval(t); window.removeEventListener("live-event", h); };
  }, [load]);

  const onOpen = (o) => {
    if (o && data.unread > 0) api.post("/alerts/read-all").then(() => setTimeout(load, 4000)).catch(() => {});
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="alerts-bell" className="relative p-2 rounded-full hover:bg-blue-50 transition-colors" aria-label="Avisos">
          <Bell size={22} weight="duotone" className="text-blue-700" />
          {data.unread > 0 && (
            <span data-testid="alerts-unread-count" className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,380px)] p-0" data-testid="alerts-panel">
        <div className="px-4 py-3 border-b font-bold text-slate-900">Avisos</div>
        <div className="max-h-[60vh] overflow-y-auto divide-y">
          {data.items.length === 0 && <p className="p-4 text-sm text-slate-500">Nenhum aviso por enquanto.</p>}
          {data.items.map((a) => {
            const { Icon, cls } = ICONS[a.type] || ICONS.revision_soon;
            return (
              <div key={a.id} data-testid={`alert-item-${a.id}`} className={`flex gap-3 p-3 ${a.read ? "" : "bg-blue-50/50"}`}>
                <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${cls}`}><Icon size={18} weight="fill" /></div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                  <div className="text-xs text-slate-600">{a.message}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{timeAgo(a.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
